<template>
  <div class="notification-container">
    <button class="notification-bell" @click="toggleNotifications">
      <i class="mdi mdi-bell"></i>
      <span v-if="hasNotifications" class="notification-badge">{{ notificationCount }}</span>
    </button>

    <!-- Notification dropdown -->
    <div v-if="showNotifications" class="notification-dropdown">
      <div class="notification-header">
        <h3>Notifications</h3>
      </div>

      <div v-if="notifications.length === 0" class="no-notifications">
        <i class="mdi mdi-bell-off"></i>
        <p>No new notifications</p>
      </div>

      <ul v-else class="notification-list">
        <b-tabs v-model="activeTab" expanded>
          <b-tab-item label="Unread">
            <li
              v-for="(notification, index) in unreadNotifications"
              :key="index"
              :class="{ 'unread': !notification.read }"
              @click="notificationAction(notification)"
            >
              <div class="notification-icon">
                <i :class="getNotificationIcon(notification.type)"></i>
              </div>
              <div class="notification-content">
                <p class="notification-text">{{ notification.message }}</p>
                <span class="notification-time">{{ formatNotificationTime(notification.createdAt) }}</span>
              </div>
            </li>
          </b-tab-item>

          <b-tab-item label="Read">
            <li
              v-for="(notification, index) in readNotifications"
              :key="index"
              :class="{ 'read': notification.read }"
              @click="notificationAction(notification)"
            >
              <div class="notification-icon">
                <i :class="getNotificationIcon(notification.type)"></i>
              </div>
              <div class="notification-content">
                <p class="notification-text">{{ notification.message }}</p>
                <span class="notification-time">{{ formatNotificationTime(notification.createdAt) }}</span>
              </div>
            </li>
          </b-tab-item>
        </b-tabs>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NotificationSystem',
  props: {
    notifications: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      showNotifications: false,
      activeTab: 0,
    };
  },
  updated() {
    console.log('NotificationSystem updated with notifications:', this.notifications);
  },
  computed: {
    sortedNotifications() {
    return [...this.notifications].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    },
    unreadNotifications() {
      return this.sortedNotifications.filter(notification => !notification.read);
    },
    readNotifications() {
      return this.sortedNotifications.filter(notification => notification.read);
    },
    notificationCount() {
      return this.unreadNotifications.length;
    },
    hasNotifications() {
      return this.notificationCount > 0;
    }
  },
  mounted() {
    document.addEventListener('click', this.closeNotificationsOnClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeNotificationsOnClickOutside);
  },
  methods: {
    toggleNotifications(event) {
      event.stopPropagation();
      this.showNotifications = !this.showNotifications;
    },
    closeNotificationsOnClickOutside(event) {
      const notificationContainer = document.querySelector('.notification-container');
      if (notificationContainer && !notificationContainer.contains(event.target)) {
        this.showNotifications = false;
      }
    },
    notificationAction(notification) {
      notification.read = true;
      this.$emit('notification-action', notification);
    },
    getNotificationIcon(type) {
      switch (type) {
        case 'verification':
          return 'mdi mdi-shield-check';
        case 'message':
          return 'mdi mdi-message-text';
        case 'review':
          return 'mdi mdi-message-draw';
        case 'system':
          return 'mdi mdi-information';
        default:
          return 'mdi mdi-bell';
      }
    },
    formatNotificationTime(timestamp) {
      if (!timestamp) {
        return 'Unknown time';
      }

      const now = this.$moment();
      const notificationTime = this.$moment(timestamp);

      if (!notificationTime.isValid()) {
        return 'Invalid date';
      }

      if (now.diff(notificationTime, 'minutes') < 1) {
        return 'Just now';
      }

      if (now.diff(notificationTime, 'hours') < 1) {
        const mins = now.diff(notificationTime, 'minutes');
        return `${mins} ${mins === 1 ? 'minute' : 'minutes'} ago`;
      }

      if (now.diff(notificationTime, 'days') < 1) {
        const hours = now.diff(notificationTime, 'hours');
        return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
      }

      if (now.diff(notificationTime, 'weeks') < 1) {
        const days = now.diff(notificationTime, 'days');
        return `${days} ${days === 1 ? 'day' : 'days'} ago`;
      }

      if (notificationTime.year() === now.year()) {
        return notificationTime.format('MMM D');
      } else {
        return notificationTime.format('MMM D, YYYY');
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.notification-container {
  position: relative;
}

.notification-bell {
  background: transparent;
  border: none;
  color: $color-warning;
  font-size: 1.5rem;
  cursor: pointer;
  position: relative;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }

  &:focus {
    outline: none;
  }
}

.notification-badge {
  position: absolute;
  top: 0;
  right: 0;
  background-color: $color-danger-strong;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  border: 2px solid white;
}

.notification-dropdown {
  position: absolute;
  top: calc(100% + 10px);
  right: -10px;
  background-color: white;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  width: 320px;
  max-height: 400px;
  overflow-y: auto;
  z-index: 1000;

  &:before {
    content: '';
    position: absolute;
    top: -8px;
    right: 18px;
    width: 16px;
    height: 16px;
    background-color: white;
    transform: rotate(45deg);
    box-shadow: -3px -3px 5px rgba(0, 0, 0, 0.04);
  }
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid $color-border-muted;
  position: sticky;
  top: 0;
  background-color: white;
  z-index: 2;

  h3 {
    font-size: 1rem;
    font-weight: 600;
    color: $color-text;
    margin: 0 auto;
  }
}

.no-notifications {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: $color-text-subtle;

  i {
    font-size: 2rem;
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  p {
    font-size: 0.9rem;
  }
}

.notification-list {
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid $color-border-muted;
    display: flex;
    align-items: flex-start;
    transition: background-color 0.2s ease;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background-color: rgba(239, 191, 65, 0.3)
    }

    &.unread {
      background-color: $color-info-soft;

      &:hover {
        background-color: $color-info-soft-strong;
      }

      .notification-text {
        font-weight: 600;
      }
    }
  }
}

.notification-icon {
  margin-right: 12px;
  font-size: 1.25rem;
  color: $color-primary;
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification-content {
  flex: 1;

  .notification-text {
    margin: 0 0 4px 0;
    font-size: 0.9rem;
    line-height: 1.4;
    color: $color-text;
  }

  .notification-time {
    font-size: 0.75rem;
    color: $color-text-subtle;
  }
}

@media screen and (max-width: 768px) {
  .notification-dropdown {
    width: 300px;
    right: -70px;

    &:before {
      right: 78px;
    }
  }
}
</style>